import { useContext, useEffect, useRef, useState } from 'react';
import type { FunctionComponent } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { Button, Drawer, Stack } from '@mantine/core';
import { useTranslation } from 'i18n';
import { PhotoBannerAlert } from 'components/MenuManagement/MenuEdit/MenuContent/PhotoBannerAlert';
import { isNotEmpty, useForm } from '@mantine/form';
import { useIntegrationFieldsVisibility } from 'components/MenuManagement/hooks/useIntegrationFieldsVisibility';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';

import { MENU_OBJECT_TYPES, TYPE_TO_READABLE_STRING_MAP } from 'constants/menuManagement';
import {
  validateMinimum,
  validateMaximum,
  validateIndividualMinimum,
  validateIndividualMaximum,
  validatePrice,
  validateClassifications,
  validateImageUrl,
  validateInternalName,
  validateServingsTag,
} from 'components/MenuManagement/validation';

import { useMenuManagement } from 'hooks/useMenuManagement';
import { getExcludedModGroups, getExcludedModifiers } from 'helpers/menuManagement';
import { logException } from 'helpers/loggers';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import { drawerEditConfig } from 'components/MenuManagement/menuManagementColumns';
import EditDrawerForm from './EditDrawerForm';

import {
  handleClassificationsChange as handleClassificationsChangeHelper,
  resetFields,
} from './helpers/formHandlers';
import { useCreateSubmitHandler } from './helpers/submitHandlers';
import {
  normalizeExcludedModGroups,
  normalizeExcludedModifiers,
  normalizeNestingAllowed,
} from './helpers/excludedModGroups';
import { useImageHandlers } from './hooks/useImageHandlers';
import type { EditDrawerRecord, EditDrawerFormValues, EditItemDrawerHandlers } from './types';

const NS = 'menuManagementEditDrawer';

const buildInitialValues = (record?: EditDrawerRecord): EditDrawerFormValues => ({
  name: record?.name || '',
  description: record?.description || '',
  mainItems: [],
  taxRate: record?.taxRate || '',
  externalId: record?.externalId || '',
  itemId: record?.uuid || '',
  minimum: record?.minimum || 0,
  maximum: record?.maximum || 0,
  multipleModifiersEnabled: !!record?.individualMaximum,
  individualMinimum: record?.individualMinimum ?? null,
  individualMaximum: record?.individualMaximum ?? null,
  price: record?.price ?? '',
  posId: record?.externalId || '',
  secondaryPrice: record?.secondaryPrice ?? '',
  classifications: record?.classifications || [],
  imageUrl: record?.imageUrl || '',
  uploadImageFile: null,
  internalName: record?.internalName || '',
  isDefault: record?.isDefault ? 'true' : 'false',
  servingsTag: record?.servingsTag ?? null,
});

type EditDrawerProps = {
  record?: EditDrawerRecord;
  editItemDrawerOpen?: boolean;
  editItemDrawerHandlers: EditItemDrawerHandlers;
  onSuccess?: () => void;
};

export const EditDrawer: FunctionComponent<EditDrawerProps> = ({
  record,
  editItemDrawerOpen,
  editItemDrawerHandlers,
  onSuccess,
}) => {
  const { t } = useTranslation(NS);
  const { customTaxRateEnabled, nestedModGroupAssignment } = useFlags();
  const { menu, isCateringMenu } = useContext(MenuEdit.Context);
  const showIntegrationFields = useIntegrationFieldsVisibility(menu);
  const [updateTaxRate, setUpdateTaxRate] = useState(false);
  const { updateCubohMenu, findMainItemParentsAndChildren, getMerchantIntegrationsFromState } =
    useMenuManagement();
  /* A POS sync-in-place menu owns every field except the merchant's own description/imagery
  (CN-50652) - the form renders those as locked with a hover notice. Kept true even for types
  with nothing editable: RenderActions blocks the drawer for them, and if one ever slips
  through, locking everything is the safe outcome. */
  const isPosSyncLocked = !!getMerchantIntegrationsFromState(record?.merchantId ?? '')
    ?.isPosSyncInPlace;
  const { children, parents } = findMainItemParentsAndChildren(record?.key, record?.itemType);
  const [selectedChildren, setSelectedChildren] = useState(children);
  const [selectedParents, setSelectedParents] = useState(parents);
  const [uploadImageFile, setUploadImageFile] = useState<File | null>(null);
  const [noEditParentChild, setNoEditParentChild] = useState(false);
  const [taxRateEnabled, setTaxRateEnabled] = useState(true);
  const [originalRecord, setOriginalRecord] = useState<EditDrawerRecord | null>(null);
  const [posLock, setPosLock] = useState(true);
  const [disablePosId, setDisablePosId] = useState(false);
  const [disableAliasName, setDisableAliasName] = useState(false);
  const [excludedModGroups, setExcludedModGroups] = useState<string[]>([]);
  const [excludedModifiers, setExcludedModifiers] = useState<string[]>([]);
  const [nestingAllowed, setNestingAllowed] = useState(false);

  const itemHasPrices = [MENU_OBJECT_TYPES.items, MENU_OBJECT_TYPES.modifiers].includes(
    record?.itemType as string,
  );

  const itemHasClassifications = [MENU_OBJECT_TYPES.items, MENU_OBJECT_TYPES.modifiers].includes(
    record?.itemType as string,
  );

  const initialValues = buildInitialValues(record);

  /* A locked drawer may only validate what the user can actually change. POS-supplied data
  routinely fails these rules (a .webp image url, a modifier with no price) and the field isn't
  even rendered to show the error on, so validating it would dead-end Save with no recourse:
  form.validate() blocks the submit on an error that has nowhere to render. imageUrl included -
  the drawer form never renders that input, photos are edited through the itemPhoto upload. The
  one field the merchant can edit here, description, has no validation rules of its own. */
  const posSyncLockedValidate = {};

  const form = useForm<EditDrawerFormValues>({
    name: 'EditItemDrawerForm',
    initialValues,
    validate: isPosSyncLocked
      ? posSyncLockedValidate
      : {
          name: isNotEmpty(t('EditDrawer.nameRequired')),
          ...(record?.itemType === MENU_OBJECT_TYPES.modifierGroups && {
            minimum: validateMinimum,
            maximum: validateMaximum,
            individualMinimum: (value, values) => {
              if (!values.multipleModifiersEnabled) return null;
              return validateIndividualMinimum(value, values);
            },
            individualMaximum: (value, values) => {
              if (!values.multipleModifiersEnabled) return null;
              return validateIndividualMaximum(value, values);
            },
          }),
          ...(itemHasPrices && {
            price: validatePrice,
          }),
          ...(itemHasClassifications && {
            classifications: validateClassifications,
          }),
          ...(record?.itemType === MENU_OBJECT_TYPES.items &&
            isCateringMenu && {
              servingsTag: validateServingsTag,
            }),
          imageUrl: validateImageUrl,
          internalName: validateInternalName,
        },
  });

  // Mantine's `useForm` returns a new object each render (and its `validate`-derived
  // methods are unstable because `validate` is rebuilt each render), so effects can't
  // safely depend on `form`. The form's underlying stores are stable, so a latest-ref
  // lets the effects below keep honest dependency arrays without re-running spuriously.
  const formRef = useRef(form);
  formRef.current = form;

  const handleClassificationsChange = handleClassificationsChangeHelper({ form });
  const handleResetFields = () =>
    resetFields({
      form,
      record: record ?? null,
      setSelectedParents,
      setSelectedChildren,
    });

  const { isUploading, handleRemoveImage, handleImageUpload } = useImageHandlers({
    form,
    setUploadImageFile,
  });

  const handleDrawerClose = () => {
    if (originalRecord) {
      form.setValues({
        ...initialValues,
        imageUrl: originalRecord.imageUrl || '',
      });
    }
    editItemDrawerHandlers.close();
    setPosLock(true);
    setUploadImageFile(null);
  };

  const handleSubmit = useCreateSubmitHandler({
    form,
    record: record!,
    menuId: menu?.uuid,
    editItemDrawerHandlers,
    updateCubohMenu,
    itemHasPrices,
    itemHasClassifications,
    selectedParents,
    selectedChildren,
    setPosLock,
    setTaxRateEnabled,
    handleImageUpload,
    onSuccess,
    isPosSyncLocked,
  });

  const isNestedModGroupRecord =
    record?.itemType === MENU_OBJECT_TYPES.modifierGroups && !!record?.isNested;
  const nestedModGroupEnabled =
    nestedModGroupAssignment && (nestingAllowed || isNestedModGroupRecord);
  const recordColumns = record
    ? drawerEditConfig(record, { nestedModGroupEnabled })[record.itemType]
    : undefined;
  const editColumns = recordColumns
    ? recordColumns.reduce(
        (accumulator: Record<string, unknown>[], current: Record<string, unknown>) => {
          if (current?.dataIndex === 'servingsTag' && !isCateringMenu) {
            return accumulator;
          }

          if (!['minimum', 'maximum'].includes(current?.dataIndex as string)) {
            accumulator.push(current);
          } else if (current?.dataIndex === 'minimum') {
            const maxColumn = recordColumns.find(
              (column: Record<string, unknown>) => column?.dataIndex === 'maximum',
            );
            accumulator.push({
              dataIndex: 'minMax',
              minimum: current,
              maximum: maxColumn,
            });
          }
          return accumulator;
        },
        [],
      )
    : [];

  useEffect(() => {
    if (editItemDrawerOpen) {
      setOriginalRecord(record ? { ...record } : null);
      formRef.current.setValues({
        ...buildInitialValues(record),
        imageUrl: record?.imageUrl || '',
        taxRate: record?.taxRate || '',
      });
      setUploadImageFile(null);
      setUpdateTaxRate(true);

      setSelectedParents(record?.parents || []);
      setSelectedChildren(record?.children || []);
    }
  }, [editItemDrawerOpen, record]);

  useEffect(() => {
    // Must run for null too - PhotoThumbnailField calls setUploadImageFile(null) after a
    // successful save, and the form field needs to clear along with it.
    formRef.current.setFieldValue('uploadImageFile', uploadImageFile);
  }, [uploadImageFile]);

  // Fetch the mod groups this modifier is not allowed to nest (enforces 1-level nesting /
  // cycle prevention) when the drawer opens. Same timing as getItemTaxRate in CustomTaxComboBox.
  useEffect(() => {
    if (
      !nestedModGroupAssignment ||
      !editItemDrawerOpen ||
      record?.itemType !== MENU_OBJECT_TYPES.modifiers
    ) {
      setExcludedModGroups([]);
      setNestingAllowed(false);
      return undefined;
    }
    // Guard against a stale response landing after the drawer switches records: if this effect
    // re-runs (or unmounts) before the fetch resolves, ignore the superseded result.
    let ignore = false;
    void (async () => {
      try {
        const data = await getExcludedModGroups({ item: record });
        if (ignore) return;
        setExcludedModGroups(normalizeExcludedModGroups(data));
        setNestingAllowed(normalizeNestingAllowed(data));
      } catch (error) {
        if (ignore) return;
        logException({
          message: 'Failed to get excluded mod groups',
          error,
          context: null,
          tags: null,
        });
        setExcludedModGroups([]);
        setNestingAllowed(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [nestedModGroupAssignment, editItemDrawerOpen, record]);

  // Fetch the nested modifiers (those that own a child mod group) that may not be added to this
  // mod group, so the Modifiers picker can disable them (BE CN-49890). This mod group may be
  // nested via a different placement (isNestedSomewhere), so the exclusion still applies even
  // when this placement is not itself nested.
  useEffect(() => {
    const modGroupIsNestedSomewhere =
      record?.itemType === MENU_OBJECT_TYPES.modifierGroups &&
      (!!record?.isNestedSomewhere || !!record?.isNested);
    if (!nestedModGroupAssignment || !editItemDrawerOpen || !modGroupIsNestedSomewhere) {
      setExcludedModifiers([]);
      return undefined;
    }
    let ignore = false;
    void (async () => {
      try {
        const data = await getExcludedModifiers({ item: record });
        if (ignore) return;
        setExcludedModifiers(normalizeExcludedModifiers(data));
      } catch (error) {
        if (ignore) return;
        logException({
          message: 'Failed to get excluded modifiers',
          error,
          context: null,
          tags: null,
        });
        setExcludedModifiers([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [nestedModGroupAssignment, editItemDrawerOpen, record]);

  const renderButtons = () => (
    <>
      <Button onClick={handleResetFields} variant="outline">
        {t('EditDrawer.reset')}
      </Button>
      <Button onClick={handleSubmit}>{t('EditDrawer.save')}</Button>
    </>
  );

  const fieldRendererProps = {
    form,
    record,
    selectedParents,
    setSelectedParents,
    selectedChildren,
    setSelectedChildren,
    excludedModGroups,
    excludedModifiers,
    nestedModGroupEnabled,
    noEditParentChild,
    setTaxRateEnabled,
    setUploadImageFile,
    handleRemoveImage,
    editItemDrawerOpen,
    isUploading,
    handleClassificationsChange,
    showIntegrationFields,
    customTaxRateEnabled,
    setNoEditParentChild,
    updateTaxRate,
    setUpdateTaxRate,
    taxRateEnabled,
    posLock,
    setPosLock,
    disablePosId,
    setDisablePosId,
    disableAliasName,
    setDisableAliasName,
    isCateringMenu,
  };

  if (!record) return null;

  return (
    <Drawer
      title={t('EditDrawer.title', {
        itemType:
          TYPE_TO_READABLE_STRING_MAP[record.itemType as keyof typeof TYPE_TO_READABLE_STRING_MAP],
      })}
      opened={editItemDrawerOpen ?? false}
      onClose={handleDrawerClose}
      variant="withDock"
    >
      {/* Type DockedDrawerFormContent (.jsx) to remove this suppression. */}
      {/* @ts-expect-error untyped .jsx primitive */}
      <DockedDrawerFormContent dockButtons={renderButtons()} mb="lg">
        <Stack gap="md">
          {record?.itemType === MENU_OBJECT_TYPES.items &&
            !record?.imageUrl &&
            !form.values.imageUrl &&
            !uploadImageFile && <PhotoBannerAlert />}
          <EditDrawerForm
            record={record}
            form={form}
            fieldRendererProps={fieldRendererProps}
            editColumns={editColumns}
            isPosSyncLocked={isPosSyncLocked}
          />
        </Stack>
      </DockedDrawerFormContent>
    </Drawer>
  );
};
